/**
 * Every user-facing sentence for External EMR patients in the Patient Manager,
 * on the chart, and in the intake's patient match, in the chart register:
 * short, exact, one claim per sentence. User-facing text says "External EMR",
 * never the vendor name.
 */
import type {
  ExternalEmrCardStatus,
  ExternalEmrCensusSyncResult,
  ExternalEmrListStatusFilter,
  ExternalEmrMovePlan,
  ExternalEmrSummary,
} from '../../../services/externalEmrPatients';
import { formatDateForDisplay } from '../../../utils/dateUtils';

type ExternalEmrMovePath = ExternalEmrMovePlan['path'];
type ExternalEmrMoveStatus = ExternalEmrMovePlan['status'];

// Patient Manager view -----------------------------------------------------------

export const ROGER_CHARTS_LABEL = 'Roger charts';
export const EXTERNAL_EMR_CHARTS_LABEL = 'External EMR charts';
export const EXTERNAL_EMR_VIEW_HELP =
  'External EMR charts are on the census until you import them into Roger.';

export function externalEmrSummaryLine(summary: ExternalEmrSummary): string {
  return [
    `${summary.shells} on the External EMR census`,
    `${summary.ready} ready to import`,
    `${summary.importing} importing`,
    `${summary.imported} imported`,
  ].join(' · ');
}

export function lastCensusLine(formatted: string): string {
  return formatted ? `Census read ${formatted}.` : 'Census not read yet.';
}

export const REFRESH_CENSUS_LABEL = 'Refresh census';
export const REFRESHING_CENSUS_LABEL = 'Reading census…';
export const REFRESHING_CENSUS_HELP = 'This can take a few minutes.';
export const CENSUS_ALREADY_RUNNING = 'The census is already being read.';
export const REFRESH_CENSUS_ERROR =
  'Unable to read the External EMR census. Please try again.';
export const SUMMARY_LOAD_ERROR = 'Unable to load External EMR counts.';
export const EXTERNAL_EMR_EMPTY =
  'No External EMR patients. Refresh the census to read them.';
/** For a viewer who cannot refresh the census: no instruction they cannot follow. */
export const EXTERNAL_EMR_EMPTY_VIEW_ONLY = 'No External EMR patients yet.';

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

/** The toast after a census refresh. Unchanged rows are not news. */
export function censusRefreshResultLine(
  result: ExternalEmrCensusSyncResult
): string {
  const created = result.created ?? 0;
  const updated = result.updated ?? 0;
  const missingCount = result.missing ?? 0;
  const parts: string[] = [];
  if (created > 0) parts.push(plural(created, 'new patient', 'new patients'));
  if (updated > 0) parts.push(`${updated} updated`);
  const missing =
    missingCount > 0
      ? ` ${plural(missingCount, 'patient is', 'patients are')} no longer on the census.`
      : '';
  const skipped =
    (result.skipped ?? 0) > 0 ? ` ${result.skipped} rows skipped.` : '';
  if (parts.length === 0 && !missing && !skipped) {
    return 'Census read. No changes.';
  }
  const changed = parts.length > 0 ? ` ${parts.join(', ')}.` : '';
  return `Census read.${changed}${missing}${skipped}`;
}

// Cards -------------------------------------------------------------------------

/** The short name of each status, for the pill and the selection groups. */
export const CARD_STATUS_LABEL: Record<ExternalEmrCardStatus, string> = {
  ready: 'Ready',
  mid_episode: 'Mid-episode',
  ending_soon: 'Ending soon',
  ready_to_cut_over: 'Ready to cut over',
  left_before_end: 'Left the census before the episode ended',
  queued: 'Queued',
  importing: 'Importing',
  failed: 'Import failed',
  imported: 'Imported',
  refreshing: 'Refreshing',
  refresh_failed: 'Refresh failed',
};

/**
 * The pill's words. Two statuses say more than their group name: an ending
 * episode names its date, and a Ready patient who left the census says so,
 * because leaving after the end is the strongest sign they are done there.
 */
export function cardStatusPillLabel(
  status: ExternalEmrCardStatus,
  {
    censusStatus,
    episodeEnd,
  }: { censusStatus?: string | null; episodeEnd?: string }
): string {
  if (status === 'ending_soon' && episodeEnd) return `Ends ${episodeEnd}`;
  if (status === 'ready' && censusStatus === 'left_census_after_end') {
    return 'Ready · left the External EMR census';
  }
  return CARD_STATUS_LABEL[status];
}

/** Why the pill says what it says, for its tooltip. */
export const CARD_STATUS_HELP: Record<ExternalEmrCardStatus, string> = {
  ready: 'The External EMR episode has ended. The chart is ready to import.',
  mid_episode:
    'The External EMR episode is still open. New work there arrives with the nightly sync while the External EMR stays on.',
  ending_soon:
    'The External EMR episode ends soon. Import after it ends to bring the whole episode.',
  ready_to_cut_over:
    'The episode ended and the External EMR already built the next period. Import, then use Add Episode to take it over.',
  left_before_end:
    'The patient left the External EMR census before the episode ended. They may be discharged there.',
  queued: 'Waiting for an import slot.',
  importing: 'Roger is copying the chart now.',
  failed: 'The import did not finish.',
  imported: 'The chart is in Roger.',
  refreshing: 'Roger is reading what changed in the External EMR.',
  refresh_failed: 'The refresh did not finish. The chart is unchanged.',
};

// Without Take Over v2 (the external-emr-take-over flag) no nightly sync
// reads an imported chart. Only a refresh brings new External EMR work.
const MID_EPISODE_HELP_BASIC =
  'The External EMR episode is still open. Work there reaches Roger only when the chart is refreshed.';

/** {@link CARD_STATUS_HELP}, with the mid-episode line for the agency's Take Over. */
export function cardStatusHelp(
  status: ExternalEmrCardStatus,
  takeOverV2: boolean
): string {
  return status === 'mid_episode' && !takeOverV2
    ? MID_EPISODE_HELP_BASIC
    : CARD_STATUS_HELP[status];
}

export function episodeRangeLine(start: string, end: string): string {
  if (!start && !end) return 'Episode: N/A';
  return `Episode: ${start || 'N/A'} – ${end || 'N/A'}`;
}

export const START_IMPORT_LABEL = 'Import chart';
export const ADD_RECERT_EPISODE_LABEL = 'Add Episode';

// Refresh an imported chart --------------------------------------------------------

export const REFRESH_CHART_LABEL = 'Refresh from External EMR';
/** Chart bar, beside the provenance line that already names the External EMR. */
export const REFRESH_CHART_SHORT_LABEL = 'Refresh';

export function lastRefreshedLine(on: string): string {
  return `Last refreshed from External EMR on ${on}.`;
}

export function importedOnLine(on: string): string {
  return `Imported from External EMR on ${on}.`;
}
export const REFRESH_CHART_HELP =
  'Brings over what changed in the External EMR. Anything edited in Roger stays as saved.';
export const REFRESH_QUEUED_TOAST = 'Refresh queued.';
export const REFRESH_CHART_ERROR =
  'Unable to start the refresh. Please try again.';
export const REFRESH_FAILED_FALLBACK =
  'The refresh did not finish. The chart is unchanged. Try again, or contact support if it fails again.';

/**
 * What a finished refresh did, in one line. Zero counts are not news; a
 * refresh that changed nothing says so. The line stays on the chart and
 * names its date when one is known.
 *
 * Medication, allergy and wound changes get their own sentence: the
 * importer counts them apart and does not split added from updated.
 */
export function refreshResultLine(
  result: {
    added?: number;
    updated?: number;
    kept_edited?: number;
    clinical_records?: number;
  },
  finishedOn: string = ''
): string {
  const added = result.added ?? 0;
  const updated = result.updated ?? 0;
  const kept = result.kept_edited ?? 0;
  const clinical = result.clinical_records ?? 0;
  const head = finishedOn
    ? lastRefreshedLine(finishedOn)
    : 'Last refreshed from External EMR.';
  const parts: string[] = [];
  if (updated > 0) parts.push(`${updated} updated`);
  if (added > 0) parts.push(`${added} added`);
  const changedParts = parts.length > 0 ? ` ${parts.join(', ')}.` : '';
  const clinicalLine =
    clinical > 0
      ? ` ${clinical === 1 ? '1 medication, allergy or wound change.' : `${clinical} medication, allergy or wound changes.`}`
      : '';
  const changed =
    changedParts || clinicalLine
      ? `${changedParts}${clinicalLine}`
      : ' No changes.';
  const keptLine =
    kept > 0
      ? ` ${kept === 1 ? '1 row edited in Roger was kept.' : `${kept} rows edited in Roger were kept.`}`
      : '';
  return `${head}${changed}${keptLine}`;
}

/** The lines the card could not fit: "and N more" for the chart's report. */
export function keptLinesMoreLine(shown: number, total: number): string | null {
  const more = total - shown;
  if (more <= 0) return null;
  return more === 1
    ? '1 more is in the import report.'
    : `${more} more are in the import report.`;
}

// Starting ----------------------------------------------------------------------

export const MID_EPISODE_CONFIRM_TITLE = 'Import mid-episode?';
export const MID_EPISODE_CONFIRM_BODY =
  'The episode is still open in the External EMR. Roger keeps reading it for this chart. New work there arrives with the nightly sync while the External EMR stays on. Import now?';
export const MID_EPISODE_CONFIRM_BODY_BASIC =
  'The episode is still open in the External EMR. Work there reaches Roger only when the chart is refreshed. Import now?';

export function midEpisodeConfirmBody(takeOverV2: boolean): string {
  return takeOverV2 ? MID_EPISODE_CONFIRM_BODY : MID_EPISODE_CONFIRM_BODY_BASIC;
}
export const CONFIRM_START_ACCEPT = 'Start import';

/** The start confirm when the External EMR already built the next period. */
export const CUTOVER_CONFIRM_TITLE = 'Import to cut over?';
export const CUTOVER_CONFIRM_BODY =
  'The External EMR already built the next period. Import, then use Add Episode to take it over.';

export function bulkConfirmTitle(count: number): string {
  return count === 1 ? 'Import 1 chart?' : `Import ${count} charts?`;
}
export const BULK_PRIORITY_LINE =
  'Importing multiple charts is a bulk-operation. If you need a specific chart faster, import just that one first.';
export function bulkMidEpisodeLine(count: number, takeOverV2: boolean): string {
  if (!takeOverV2) {
    return count === 1
      ? '1 is mid-episode. Work there reaches Roger only when the chart is refreshed.'
      : `${count} are mid-episode. Work there reaches Roger only when each chart is refreshed.`;
  }
  return count === 1
    ? '1 is mid-episode. New work there arrives with the nightly sync while the External EMR stays on.'
    : `${count} are mid-episode. New work there arrives with the nightly sync while the External EMR stays on.`;
}

export function bulkCutoverLine(count: number): string {
  return count === 1
    ? '1 already has the next period built in the External EMR. Import, then use Add Episode to take it over.'
    : `${count} already have the next period built in the External EMR. Import, then use Add Episode to take each over.`;
}

export function startImportsLabel(count: number): string {
  return `Start import (${count})`;
}
export function cancelImportsLabel(count: number): string {
  return `Cancel import (${count})`;
}
export const NONE_TO_START_REASON = 'No selected chart can start an import.';
// Shown in the selection bar while the agency cannot import. The header
// above the list carries the full reason.
export const START_BLOCKED_NOTICE =
  'Import cannot start now. The reason is at the top of the page.';
// Shown on Start while the agency's readiness read has not answered.
export const START_PENDING_REASON = 'Checking the External EMR login…';
export const NONE_TO_CANCEL_REASON =
  'No selected chart is queued or importing.';

export const START_IMPORT_ERROR =
  'Unable to start the import. Please try again.';
export const CANCEL_IMPORTS_ERROR =
  'Unable to cancel every import. Please try again.';
export const IMPORT_QUEUED_TOAST = 'Import queued.';

/** The toast after a bulk start. Refused charts stay selected. */
export function bulkStartResultLine(started: number, refused: number): string {
  const head =
    started === 0
      ? 'No imports started.'
      : `${plural(started, 'import', 'imports')} queued.`;
  if (refused === 0) return head;
  const tail =
    refused === 1
      ? '1 chart could not start and stays selected.'
      : `${refused} charts could not start and stay selected.`;
  return `${head} ${tail}`;
}

export function bulkCancelResultLine(canceled: number): string {
  return `${plural(canceled, 'import', 'imports')} canceled.`;
}

// Chart page --------------------------------------------------------------------

export const SHELL_PAGE_TITLE = 'Not imported yet';
export const SHELL_PAGE_BODY =
  'Roger knows this patient from the External EMR census. The chart will be available in Roger once imported.';
export const SHELL_PAGE_ADD_EPISODE_LINE =
  'Adding an episode requires importing this patient first.';
export const ADD_EPISODE_UNAVAILABLE_TITLE = 'Add Episode is not open.';
export const LAST_EPISODE_ENDED_LINE = 'The last episode has ended.';
export const TAKEOVER_DUE_LINE =
  'The External EMR built the next period. Use Add Episode to take it over.';
export const TAKE_OVER_LABEL = 'Take over';
export const TAKE_OVER_WAIT_FOR_REFRESH =
  'Available when the refresh finishes.';
export const TAKE_OVER_READY_HELP =
  'Moves this chart to Roger. You choose the day Roger charts from.';
export const RENDERS_PENDING_LINE =
  'Documents still arriving from the External EMR.';
export const RENDERS_UNRECOVERED_LINE =
  'Some documents could not be brought over from the External EMR.';
export const HISTORY_PENDING_LINE =
  'Earlier episodes still arriving from the External EMR.';
export const HISTORY_FAILED_LINE =
  'Earlier episodes could not be brought over from the External EMR.';
/**
 * The next step after HISTORY_FAILED_LINE, for a viewer who can refresh the
 * chart: a full refresh re-reads it. No instruction they cannot follow.
 */
export const HISTORY_FAILED_REFRESH_HINT = `${REFRESH_CHART_LABEL} to try again.`;
/** In place of Refresh while the backfill runs: the server refuses it then. */
export const HISTORY_ARRIVING_REFRESH_LINE =
  'Earlier episodes are still arriving. Refresh when they land.';

// One import: queue, cancel, retry ------------------------------------------------

export const CANCEL_IMPORT_LABEL = 'Cancel import';
export const RETRY_IMPORT_LABEL = 'Retry import';
export const CANCEL_IMPORT_ERROR =
  'Unable to cancel the import. Please try again.';
export const IMPORT_FAILED_FALLBACK =
  'The import did not finish. Retry, or contact support if it fails again.';

/**
 * `position` is the server's queue position: 1 is the next chart to start,
 * so the charts ahead number one fewer.
 */
export function queuePositionLine(position: number): string {
  const ahead = position - 1;
  if (ahead <= 0) return 'Next in queue.';
  return ahead === 1
    ? '1 chart is ahead of this one.'
    : `${ahead} charts are ahead of this one.`;
}

// Census table: when each chart moves to Roger (EMR-2891) -------------------------

export const CENSUS_COLUMN = {
  patient: 'Patient',
  episodeStart: 'Episode start',
  episodeEnd: 'Episode end',
  recert: 'Recert',
  path: 'Path',
  chartsFrom: 'Roger charts from',
  moveBy: 'Move by',
  status: 'Status',
  actions: 'Import',
} as const;

export const RECERT_HELP =
  "The External EMR's episode ends this day; its next episode starts the day after.";

export const PATH_LABEL: Record<ExternalEmrMovePath, string> = {
  recert: 'Recert',
  take_over: 'Take Over',
};

const PATH_HELP: Record<ExternalEmrMovePath, string> = {
  recert: 'Moves to Roger with the recert the External EMR builds.',
  take_over: 'Needs Take Over before the last day in the External EMR.',
};

/** The Path tooltip. A Take Over row names the last day when one is known. */
export function pathHelp(
  path: ExternalEmrMovePath,
  lastDay?: string | null
): string {
  if (path === 'take_over' && lastDay) {
    return `Needs Take Over by ${formatDateForDisplay(lastDay)}, the last day in the External EMR.`;
  }
  return PATH_HELP[path];
}

export const MOVE_STATUS_LABEL: Record<ExternalEmrMoveStatus, string> = {
  moved: 'Moved',
  slated: 'Slated',
  due_soon: 'Due soon',
  overdue: 'Overdue',
};

export const MOVE_STATUS_HELP: Record<ExternalEmrMoveStatus, string> = {
  moved: 'The chart moved to Roger. Chart all new work in Roger.',
  slated: 'Not moved to Roger yet. The move-by day is still ahead.',
  due_soon: 'Not moved to Roger yet. The day it moves by is within a week.',
  overdue: 'Not moved to Roger, and the move-by day has passed.',
};

// Acting on the recommendations (EMR-2915) ----------------------------------------

/** The header and signal line: how many charts still need a person's Take Over. */
export function takeOverPendingLine(count: number, lastDay: string): string {
  const day = formatDateForDisplay(lastDay);
  return count === 1
    ? `1 chart needs Take Over by ${day}.`
    : `${count} charts need Take Over by ${day}.`;
}

export const SEE_THE_CHARTS_LABEL = 'See the charts';

/** The status chips, in the order they show. */
export const STATUS_FILTERS: readonly ExternalEmrListStatusFilter[] = [
  'needs_take_over',
  'overdue',
  'due_soon',
  'slated',
  'moved',
];

const STATUS_FILTER_LABEL: Record<ExternalEmrListStatusFilter, string> = {
  needs_take_over: 'Needs Take Over',
  ...MOVE_STATUS_LABEL,
};

export function statusChipLabel(
  filter: ExternalEmrListStatusFilter,
  count: number
): string {
  return `${STATUS_FILTER_LABEL[filter]} (${count})`;
}

/** The list's empty state when a chip filters every row out. In a sentence, so lower case. */
export function statusEmptyLine(filter: ExternalEmrListStatusFilter): string {
  return filter === 'needs_take_over'
    ? 'No charts need Take Over.'
    : `No charts are ${STATUS_FILTER_LABEL[filter].toLowerCase()}.`;
}

export const TAKE_OVER_CONFIRM_LABEL = 'Confirm';
export const TAKE_OVER_CONFIRM_HELP =
  'Opens Take Over for this date. Nothing moves until you confirm there.';
export const TAKE_OVER_NOT_OPEN = 'Take Over is not open on this chart.';
export const TAKE_OVER_OPEN_ERROR =
  'Unable to open Take Over. Please try again.';
/** Under a shell's date: Take Over needs the chart in Roger first. */
export const IMPORT_FIRST_LINE = 'Import first.';
export const EDIT_CHARTS_FROM_LABEL = 'Edit';
export const CHARTS_FROM_CHANGE_ERROR =
  'Unable to change the day Roger charts from.';

/**
 * The toast after the ratified day changed. It says only what the day
 * changes: where imported visits are managed. Who bills them does not
 * follow the day: visits inside a claim the External EMR keeps stay on its
 * claim, and visits on a claim Roger bills stay on Roger's (Daniel,
 * 2026-10-19). Visits are counted, never blocked.
 */
export function chartsFromChangedLine(
  day: string,
  nowRoger: number,
  nowExternal: number,
  onExternalClaims = 0,
  onRogerClaims = 0
): string {
  const head = `Roger charts from ${day}.`;
  if (nowRoger > 0) {
    const managed = `${head} ${plural(nowRoger, 'visit is', 'visits are')} now managed in Roger.`;
    if (onExternalClaims <= 0) return managed;
    if (onExternalClaims >= nowRoger) {
      return nowRoger === 1
        ? `${managed} Its claim stays with the External EMR.`
        : `${managed} Their claims stay with the External EMR.`;
    }
    return onExternalClaims === 1
      ? `${managed} The claim for 1 of them stays with the External EMR.`
      : `${managed} Claims for ${onExternalClaims} of them stay with the External EMR.`;
  }
  if (nowExternal > 0) {
    const managed = `${head} ${plural(nowExternal, 'visit is', 'visits are')} now managed in the External EMR.`;
    if (onRogerClaims <= 0) return managed;
    if (onRogerClaims >= nowExternal) {
      return nowExternal === 1
        ? `${managed} Roger still bills its claim.`
        : `${managed} Roger still bills their claims.`;
    }
    return onRogerClaims === 1
      ? `${managed} Roger still bills the claim for 1 of them.`
      : `${managed} Roger still bills claims for ${onRogerClaims} of them.`;
  }
  return head;
}

export function editChartsFromLabel(name: string): string {
  return `Edit Roger charts from, ${name}`;
}

export function daysLeftLine(days: number): string {
  if (days === 0) return 'Due today';
  if (days > 0) return days === 1 ? '1 day left' : `${days} days left`;
  return days === -1 ? '1 day overdue' : `${-days} days overdue`;
}

export const CHARTS_FROM_ESTIMATE_HELP =
  "Estimated from 30-day claim periods. Take Over confirms the day from the payer's real periods.";
export const RECERT_CHARTS_FROM =
  'Moves with the recert, so Take Over proposes no day.';
export const PLANNED_CHIP = 'Planned';
export const PLANNED_CHIP_HELP =
  'Planned on the census, not estimated. Take Over starts from this day.';
export const USE_PROPOSAL_LABEL = 'Use the proposal';
export const PLAN_SAVE_ERROR = 'Unable to save the planned day.';

export function plannedChartsFromSavedLine(displayDay: string): string {
  return `Roger charts from ${displayDay} planned on the census.`;
}

export function plannedNoLongerFitsLine(proposed: string | null): string {
  return proposed
    ? `Planned day no longer fits; Roger proposes ${proposed}.`
    : 'Planned day no longer fits.';
}

export function chartsFromPickerLabel(name: string): string {
  return `Roger charts from, ${name}`;
}

// Intake patient match ------------------------------------------------------------

/** Source chip on a shell row: Roger knows the patient, but holds no chart. */
export const MATCH_SHELL_SOURCE_LABEL = 'External EMR';
export const MATCH_SHELL_NOT_IMPORTED =
  'Import this patient from Patient Manager first.';
